/* eslint-disable prettier/prettier */
import * as React from "react";
import { OutlinedInput, Box } from "@mui/material";
import FormHelperText from "@mui/material/FormHelperText";
import CardContent from "@mui/material/CardContent";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import Button from "src/components/shared/atoms/button";
import UPDATE_LABEL_MUTATION from "src/graphql/mutation/settings/updateUserLabel";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { useFeature } from "src/context/global";
import useStyles from "./Styles";

interface IlabelData {
  agentId: string;
  author: string;
  createdAt: string; // Use string if the timestamp is stored as a string, or use number if you want it to be a number.
  id: string;
  label: string;
  mainSuperAdminId: string;
  type: "personal" | "generic"; // Use a union type to restrict to specific string values.
}

interface UpdateLabelProps {
  labelData: IlabelData;
}

const UpdateLabel = ({ labelData }: UpdateLabelProps) => {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const { canUse } = useFeature();
  const canSavePersonalLabels = canUse("can_save_personal_labels");
  const canSaveAdminLabels = canUse("can_save_admin_labels");

  const canSave =
    labelData?.type === "personal" ? canSavePersonalLabels : canSaveAdminLabels;

  const navigate = useNavigate();

  const [initialFormikValues] = React.useState({
    text: (labelData && labelData.label) || "",
    // type: "personal",
  });

  const [
    updateLabel,
    {
      data: updateLabelMutationResult,
      loading: updateLabelMutationLoading,
      error: updateLabelMutationError,
    },
  ] = useMutation(UPDATE_LABEL_MUTATION);

  React.useEffect(() => {
    if (updateLabelMutationError) {
      enqueueSnackbar("Something went wrong.", {
        variant: "error",
      });
    }
  }, [updateLabelMutationError]);

  React.useEffect(() => {
    if (
      updateLabelMutationResult &&
      updateLabelMutationResult.updateUserLabel
    ) {
      navigate("/settings/label");
    }
  }, [updateLabelMutationResult]);

  const validationSchema = Yup.object().shape({
    text: Yup.string()
      .required("Label is required")
      .min(3, "Minimum 3 characters are required")
      .max(25, "Must not exceed 25 characters"),
  });

  interface FormValuesProps {
    text: string;
  }

  const updatedHandleSubmit = async (
    values: FormValuesProps,
    formikHelpers: any,
  ) => {
    formikHelpers.setSubmitting(false);
    try {
      updateLabel({
        variables: {
          labelId: labelData.id,
          label: values.text,
        },
      });
    } catch (error) {
      enqueueSnackbar("Something went wrong.", {
        variant: "error",
      });
    }
  };
  const formik = useFormik({
    initialValues: initialFormikValues,
    validationSchema,
    onSubmit: updatedHandleSubmit,
  });

  return (
    <Box>
      <CardContent className={classes.cardContent}>
        <Box component="form" onSubmit={formik.handleSubmit}>
          <OutlinedInput
            className={classes.fieldStyle}
            name="text"
            value={formik.values.text}
            onChange={formik.handleChange}
            onError={formik.touched.text && Boolean(formik.errors.text)}
          />
          <Box className={classes.formikText}>
            {formik.touched.text && formik.errors.text && (
              <FormHelperText error>{formik.errors.text}</FormHelperText>
            )}
          </Box>
          <Button
            size="large"
            variant="contained"
            color="primary"
            type="submit"
            className={classes.savebutton}
            circular={updateLabelMutationLoading}
            disabled={formik.values.text === labelData.label || !canSave}
          >
            Save
          </Button>
        </Box>
      </CardContent>
    </Box>
  );
};

export default UpdateLabel;
